/**
 * Hover and focus help, replacing the browser's `title` attribute.
 *
 * `title` waits about a second, cannot be styled, never appears for a
 * keyboard user and never appears on a touch screen. This shows after a short
 * delay on hover, at once on keyboard focus, toggles on a tap, and closes on
 * Escape, on scroll and when the pointer leaves.
 *
 * Rendered into `document.body` with fixed positioning: the due list scrolls
 * inside its own box (for the sticky header), and anything positioned inside
 * that box would be clipped at its edges.
 *
 * `focusable` (default true) makes the wrapper a tab stop. Pass false when the
 * child is already focusable (a button), or for help repeated on every row of
 * a long table, where a tab stop per row would make the keyboard walk through
 * thousands of them.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const OPEN_DELAY_MS = 150
const GAP_PX = 6
const EDGE_PX = 8

export function Tooltip({
  content,
  children,
  focusable = true,
  className,
}: {
  content: ReactNode
  children: ReactNode
  focusable?: boolean
  className?: string
}) {
  const id = useId()
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  const clearTimer = () => {
    if (timer.current !== undefined) window.clearTimeout(timer.current)
    timer.current = undefined
  }
  const show = useCallback((delay: number) => {
    clearTimer()
    if (delay === 0) setOpen(true)
    else timer.current = window.setTimeout(() => setOpen(true), delay)
  }, [])
  const hide = useCallback(() => {
    clearTimer()
    setOpen(false)
    setPosition(null)
  }, [])

  useEffect(() => clearTimer, [])

  // Place it above the trigger, or below when there is no room above; keep it
  // inside the viewport sideways.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tipRef.current) return
    const anchor = triggerRef.current.getBoundingClientRect()
    const tip = tipRef.current.getBoundingClientRect()
    const above = anchor.top - tip.height - GAP_PX
    const top = above >= EDGE_PX ? above : anchor.bottom + GAP_PX
    const centred = anchor.left + anchor.width / 2 - tip.width / 2
    const left = Math.min(Math.max(centred, EDGE_PX), window.innerWidth - tip.width - EDGE_PX)
    setPosition({ top, left })
  }, [open, content])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    const onPointerDown = (event: PointerEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) hide()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [open, hide])

  if (content === null || content === undefined || content === '') return <>{children}</>

  return (
    <>
      <span
        ref={triggerRef}
        className={className ?? 'inline-flex'}
        tabIndex={focusable ? 0 : undefined}
        aria-describedby={open ? id : undefined}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') show(OPEN_DELAY_MS)
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') hide()
        }}
        onPointerDown={(event) => {
          // A tap is the only way to ask on a touch screen.
          if (event.pointerType !== 'mouse') {
            if (open) hide()
            else show(0)
          }
        }}
        onFocus={() => show(0)}
        onBlur={hide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="pointer-events-none fixed z-50 max-w-[18rem] rounded-eq-sm bg-eq-navy px-2.5 py-1.5 text-[12px] leading-snug text-white shadow-eq-raised print:hidden"
            style={
              position === null
                ? { top: 0, left: 0, visibility: 'hidden' }
                : { top: position.top, left: position.left }
            }
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}
